import { ExternalLink } from "lucide-react";
import { OPERATION_LABEL, STATUS_LABEL } from "@/lib/studio";
import { formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

const TONE = { available: "bg-emerald-400/15 text-emerald-200", price_pending: "bg-amber-300/15 text-amber-200", unverified: "bg-white/10 text-zinc-400" };

export function StatusBadge({ status }) {
  return <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", TONE[status])} data-testid={`model-status-${status}`}>{STATUS_LABEL[status]}</span>;
}

export function ModelRow({ m, selected, onSelect }) {
  const ready = m.status === "available";
  return (
    <button type="button" onClick={() => ready && onSelect?.(m)} aria-disabled={!ready} data-testid={`model-option-${m.id.replace(/[^a-z0-9]+/gi, "-")}`}
      className={cn("flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors", selected ? "border-white/40 bg-white/[0.07]" : "border-white/[0.08] bg-ink-900",
        ready ? "hover:border-white/25" : "cursor-not-allowed opacity-60")}>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-zinc-100">{m.family} <StatusBadge status={m.status} /></span>
        <span className="mt-0.5 block text-xs text-zinc-500">{m.workflow}{m.workflow.toLowerCase().includes((OPERATION_LABEL[m.operation] || m.operation).toLowerCase()) ? "" : ` · ${OPERATION_LABEL[m.operation] || m.operation}`}</span>
        {!ready && m.status_reason && <span className="mt-1 block text-[11px] text-zinc-500">{m.status_reason}</span>}
      </span>
      <span className="shrink-0 text-right text-xs tabular-nums text-zinc-300">{m.from_credits ? `from ${formatNumber(m.from_credits)} cr` : "—"}</span>
    </button>
  );
}

export function ModelPicker({ models, selected, onSelect, testId = "model-picker" }) {
  const sorted = [...models].sort((a, b) => (a.status === "available" ? 0 : 1) - (b.status === "available" ? 0 : 1) || (a.from_credits ?? 1e9) - (b.from_credits ?? 1e9));
  return (
    <div className="grid max-h-[340px] gap-2 overflow-y-auto pr-1 sm:grid-cols-2" data-testid={testId}>
      {sorted.map((m) => <ModelRow key={m.id} m={m} selected={selected?.id === m.id} onSelect={onSelect} />)}
    </div>
  );
}

export function DocLink({ m }) {
  return <a href={m.doc} target="_blank" rel="noreferrer" className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[11px] text-zinc-500 hover:text-zinc-200" data-testid="model-doc-link">Model docs <ExternalLink className="h-3 w-3" /></a>;
}
